import {
  Alert,
  Box,
  Button,
  Divider,
  Icons,
  iconSize,
  Link,
  Menu,
  MenuItem,
  palette,
  Stack,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FILE_STAGES,
  type FileStage,
  type FormFormat,
  type FormKind,
  type FormSummary,
} from '@munifocus/contracts-gov';
import { useForms, useGenerateForm } from '../lib/forms-api';
import { formatDateTime } from '../lib/relative-time';
import { DocumentLifecycle } from './DocumentLifecycle';
import { UnavailableDownload } from './UnavailableDownload';

/**
 * The whole-file document surface: every D&U workflow document the system can
 * assemble from the file record, each with a Generate action and a download
 * link once produced. Documents are listed in workflow order (intake →
 * escalation). Individual step screens still own their in-context statutory
 * form (Step 5 Form 4, Step 8 Form 5); this is the officer's one place to
 * produce and export any document for the file.
 */
const CATALOG: readonly FormKind[] = [
  'complaint-report',
  'consent-to-enter',
  'inspection-report',
  'courtesy-letter',
  'notice-to-comply',
  'legal-checklist',
  'affidavit-of-service',
  'appeal-committee-report',
  'revenue-tax-inquiry',
  'council-resolution',
  'reimbursement-request',
  'discharge-certificate',
];

/**
 * The earliest stage at which each document can legitimately be produced.
 *
 * Every one of these is a step in a statutory sequence, and the catalog used to
 * offer all eleven from the moment a file was created — so a file still at
 * intake could generate a Form 4 notice to comply (Step 5) or a Form 5 discharge
 * certificate (Step 8), neither of which has any lawful basis before the
 * preceding steps have happened.
 *
 * Availability is cumulative: a document stays available once its stage is
 * reached, because an officer at escalation still needs the complaint report.
 * `closed` is deliberately absent — every document remains reachable on a closed
 * file, which is what makes the record exportable afterwards.
 */
const AVAILABLE_FROM: Record<FormKind, FileStage> = {
  'complaint-report': 'intake',
  'consent-to-enter': 'review',
  'courtesy-letter': 'review',
  'inspection-report': 'site_visit',
  'notice-to-comply': 'escalation',
  'discharge-certificate': 'escalation',
  'legal-checklist': 'escalation',
  'affidavit-of-service': 'escalation',
  'appeal-committee-report': 'escalation',
  'revenue-tax-inquiry': 'escalation',
  'council-resolution': 'escalation',
  'reimbursement-request': 'escalation',
};

/**
 * Documents the file has reached the stage for, in workflow order. The Appeal
 * Committee report is built from the appeals module's record, so it is offered
 * only where that module is switched on for the file's municipality.
 */
function availableAt(stage: FileStage, appealReport: boolean): FormKind[] {
  const reached = FILE_STAGES.indexOf(stage);
  return CATALOG.filter(
    (kind) =>
      FILE_STAGES.indexOf(AVAILABLE_FROM[kind]) <= reached &&
      (appealReport || kind !== 'appeal-committee-report'),
  );
}

/** The two export formats, in the order officers pick them. */
const FORMATS = [
  {
    format: 'docx',
    icon: Icons.FileWord,
    labelKey: 'gov.forms.formatWord',
    hintKey: 'gov.forms.formatWordHint',
  },
  {
    format: 'pdf',
    icon: Icons.FilePdf,
    labelKey: 'gov.forms.formatPdf',
    hintKey: 'gov.forms.formatPdfHint',
  },
] as const satisfies readonly {
  format: FormFormat;
  icon: typeof Icons.FileWord;
  labelKey: string;
  hintKey: string;
}[];

/** The same metadata, keyed by format, for labelling a generated download. */
const FORMAT_BY_KEY = Object.fromEntries(FORMATS.map((f) => [f.format, f])) as Record<
  FormFormat,
  (typeof FORMATS)[number]
>;

export function DocumentCatalog({
  fileId,
  stage,
  appealReport = false,
  canGenerate = true,
}: {
  fileId: string;
  stage: FileStage;
  /** Offer the Appeal Committee report — true where the appeals module is on. */
  appealReport?: boolean;
  /** False for a read-only seat: the documents already made stay downloadable. */
  canGenerate?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const forms = useForms(fileId);
  const generate = useGenerateForm(fileId);

  // Every version of every kind, newest first, for the lifecycle block below.
  const versionsByKind = new Map<FormKind, FormSummary[]>();
  for (const f of forms.data ?? []) {
    const existing = versionsByKind.get(f.kind);
    if (existing) existing.push(f);
    else versionsByKind.set(f.kind, [f]);
  }

  // The one-shot generate path is separate from authoring: it produces a
  // finished copy in the format the officer asked for, and is what most kinds
  // are used through. A kind can be generated in both formats, so key by kind
  // *and* format — keying by kind alone collapsed the two into one. Keep only
  // the newest copy of each format; the API returns rows newest-first, so the
  // first one seen per format wins. Drafts are excluded: they have no finished
  // copy to offer.
  const generatedByKind = new Map<FormKind, FormSummary[]>();
  const seen = new Set<string>();
  for (const f of forms.data ?? []) {
    if (f.status === 'draft') continue;
    const slot = `${f.kind}:${f.format}`;
    if (seen.has(slot)) continue;
    seen.add(slot);
    const existing = generatedByKind.get(f.kind);
    if (existing) existing.push(f);
    else generatedByKind.set(f.kind, [f]);
  }

  // Format menu: clicking Generate opens a small "Word / PDF" chooser.
  const [menu, setMenu] = useState<{ anchor: HTMLElement; kind: FormKind } | null>(null);
  const openMenu = (e: MouseEvent<HTMLElement>, kind: FormKind) =>
    setMenu({ anchor: e.currentTarget, kind });
  const closeMenu = () => setMenu(null);

  const onGenerate = (kind: FormKind, format: FormFormat) => {
    closeMenu();
    generate.mutate(
      { kind, format },
      {
        onSuccess: () =>
          snackbar.success(
            t('gov.forms.generateSuccess', {
              name: t(`gov.forms.kind.${kind}`),
              format: format.toUpperCase(),
            }),
          ),
        onError: () => snackbar.error(t('gov.forms.generateError')),
      },
    );
  };

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="overline">{t('gov.forms.catalog')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('gov.forms.catalogHint')}
        </Typography>
      </Box>

      {generate.isError && <Alert severity="error">{t('gov.forms.generateError')}</Alert>}

      <Stack divider={<Divider flexItem />}>
        {availableAt(stage, appealReport).map((kind) => {
          const docs = generatedByKind.get(kind) ?? [];
          const pending = generate.isPending && generate.variables?.kind === kind;
          return (
            <Stack
              key={kind}
              direction="row"
              sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, py: 1 }}
            >
              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                <Typography variant="body2">{t(`gov.forms.kind.${kind}`)}</Typography>

                <DocumentLifecycle
                  fileId={fileId}
                  kind={kind}
                  forms={versionsByKind.get(kind) ?? []}
                />
                {docs.map((doc) => {
                  const meta = FORMAT_BY_KEY[doc.format];
                  const FormatIcon = meta.icon;
                  const formatName = t(meta.labelKey);
                  return doc.url ? (
                    <Link
                      key={doc.id}
                      href={doc.url}
                      target="_blank"
                      rel="noopener"
                      underline="hover"
                      aria-label={t('gov.forms.downloadAria', {
                        name: t(`gov.forms.kind.${kind}`),
                        format: formatName,
                      })}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                    >
                      <FormatIcon size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                      {/* Name the format on the row itself — "download" alone left
                          the officer guessing which of the two copies they'd open. */}
                      <Typography variant="caption">
                        {`${formatName} · ${formatDateTime(doc.generatedAt, i18n.language)}`}
                      </Typography>
                      <Icons.Download size={iconSize.xs} style={{ flexShrink: 0 }} />
                    </Link>
                  ) : (
                    // No stored file behind the row (#418): name the format, but
                    // never the timestamp — it would imply a downloadable copy.
                    // The accessible name carries the format too, so two
                    // ungenerated rows don't announce identically.
                    <UnavailableDownload
                      key={doc.id}
                      accessibleLabel={t('gov.forms.notGeneratedFormatHint', {
                        format: formatName,
                      })}
                    >
                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{ alignItems: 'center', minWidth: 0 }}
                      >
                        <FormatIcon size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                        <Typography variant="caption">
                          {`${formatName} · ${t('gov.forms.notGenerated')}`}
                        </Typography>
                      </Stack>
                    </UnavailableDownload>
                  );
                })}
              </Stack>
              {canGenerate && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icons.FileText size={iconSize.md} />}
                  endIcon={<Icons.ChevronDown size={iconSize.sm} />}
                  disabled={pending}
                  onClick={(e) => openMenu(e, kind)}
                >
                  {pending ? t('gov.forms.generating') : t('gov.forms.generate')}
                </Button>
              )}
            </Stack>
          );
        })}
      </Stack>

      <Menu
        anchorEl={menu?.anchor ?? null}
        open={Boolean(menu)}
        onClose={closeMenu}
        // Hang the sheet under the button's right edge rather than its left, so
        // it opens inward and can't overflow the content column.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 264,
              borderRadius: 2,
              border: `1px solid ${palette.border.onWhite}`,
            },
          },
          list: { sx: { py: 0.5 } },
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: 'block', px: 2, pt: 0.75, pb: 0.5, color: palette.text.muted }}
        >
          {t('gov.forms.formatHeading')}
        </Typography>

        {FORMATS.map(({ format, icon: Icon, labelKey, hintKey }) => (
          <MenuItem
            key={format}
            onClick={() => menu && onGenerate(menu.kind, format)}
            sx={{ gap: 1.5, px: 2, py: 1.25, alignItems: 'flex-start' }}
          >
            <Icon size={22} strokeWidth={1.75} style={{ flexShrink: 0, marginTop: 1 }} />
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
                {t(labelKey)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: palette.text.muted, lineHeight: 1.35 }}>
                {t(hintKey)}
              </Typography>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
}
